import { z } from 'zod';
import { INTERNAL_ONLY, type Visibility } from '../../domain/entities/common.ts';
import {
  FolderKindSchema,
  FolderSchema,
  type File,
  type Folder,
  type FolderKind,
} from '../../domain/entities/file.ts';
import { newId } from '../../domain/id.ts';
import type { ClockPort } from '../../ports/clock-port.ts';
import type { DataPort } from '../../ports/data-port.ts';
import { NotFoundError } from '../../ports/repository.ts';

/**
 * Folders inside a job's three file roots.
 *
 * The roots themselves (`documents`, `photos`, `videos`) are not stored:
 * they are derived from a file's content type, so a photograph cannot be
 * filed under Documents by mistake and no job needs three folders created
 * before anything can be uploaded. A stored folder always names the root it
 * sits in, which is what makes "move this into that folder" checkable.
 */
export class FolderError extends Error {
  readonly reason: 'invalid' | 'notEmpty' | 'system' | 'cycle';

  constructor(reason: 'invalid' | 'notEmpty' | 'system' | 'cycle', message: string) {
    super(message);
    this.name = 'FolderError';
    this.reason = reason;
  }
}

export interface FolderContext {
  readonly data: DataPort;
  readonly clock: ClockPort;
  readonly companyId: string;
  readonly uid: string;
}

export const CreateFolderInputSchema = z.object({
  name: z.string().trim().min(1, 'A folder needs a name').max(120),
  kind: FolderKindSchema,
  parentId: z.string().min(1).optional(),
  visibility: z.custom<Visibility>().optional(),
});
export type CreateFolderInput = z.input<typeof CreateFolderInputSchema>;

function folders(context: { data: DataPort; companyId: string }, jobId: string) {
  return context.data.collection<Folder>({ companyId: context.companyId, jobId }, 'folders');
}

function files(context: { data: DataPort; companyId: string }) {
  return context.data.collection<File>({ companyId: context.companyId }, 'files');
}

/** Every folder on a job, optionally just one root's. */
export async function listFolders(
  context: { data: DataPort; companyId: string },
  jobId: string,
  kind?: FolderKind,
): Promise<Folder[]> {
  const page = await folders(context, jobId).list({
    orderBy: [{ field: 'name', direction: 'asc' }],
  });
  const items = [...page.items];
  return kind === undefined ? items : items.filter((folder) => folder.kind === kind);
}

export async function createFolder(
  context: FolderContext,
  jobId: string,
  input: CreateFolderInput,
): Promise<Folder> {
  const parsed = CreateFolderInputSchema.safeParse(input);
  if (!parsed.success) throw new FolderError('invalid', parsed.error.issues[0]!.message);

  const repo = folders(context, jobId);
  if (parsed.data.parentId) {
    const parent = await repo.get(parsed.data.parentId);
    if (!parent) throw new NotFoundError('folders', parsed.data.parentId);
    // A folder's root is its parent's root, not whatever the request said.
    // Otherwise a subfolder of Photos could claim to be in Documents and
    // then be a legal destination for a PDF.
    if (parent.kind !== parsed.data.kind) {
      throw new FolderError('invalid', `That folder is in ${parent.kind}, not ${parsed.data.kind}`);
    }
  }

  const now = context.clock.now().toISOString();
  const folder: Folder = {
    id: newId(),
    companyId: context.companyId,
    jobId,
    name: parsed.data.name,
    parentId: parsed.data.parentId,
    kind: parsed.data.kind,
    visibility: parsed.data.visibility ?? INTERNAL_ONLY,
    system: false,
    createdAt: now,
    createdBy: context.uid,
    updatedAt: now,
    updatedBy: context.uid,
  };

  const checked = FolderSchema.safeParse(folder);
  if (!checked.success) throw new FolderError('invalid', checked.error.issues[0]!.message);

  await repo.create(folder);
  return folder;
}

export async function renameFolder(
  context: FolderContext,
  jobId: string,
  id: string,
  name: string,
): Promise<Folder> {
  const trimmed = name.trim();
  if (trimmed.length === 0) throw new FolderError('invalid', 'A folder needs a name');

  const repo = folders(context, jobId);
  const existing = await repo.get(id);
  if (!existing) throw new NotFoundError('folders', id);
  // Mirrors the rule, which refuses the same write. Stated here so the UI
  // can disable the control rather than offer an action that will fail.
  if (existing.system) throw new FolderError('system', `"${existing.name}" cannot be renamed`);

  const patch = {
    name: trimmed,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await repo.update(id, patch);
  return { ...existing, ...patch };
}

/**
 * Re-parent a folder, or lift it to its root.
 *
 * Refuses a move into the folder's own subtree. Allowing it would detach
 * the whole branch from every root: nothing would list it, and nothing
 * would report an error either — the files would simply be gone from the
 * only view that reaches them.
 */
export async function moveFolder(
  context: FolderContext,
  jobId: string,
  id: string,
  parentId?: string,
): Promise<Folder> {
  const repo = folders(context, jobId);
  const existing = await repo.get(id);
  if (!existing) throw new NotFoundError('folders', id);
  if (existing.system) throw new FolderError('system', `"${existing.name}" cannot be moved`);

  const all = await listFolders(context, jobId);
  if (parentId !== undefined) {
    const parent = all.find((folder) => folder.id === parentId);
    if (!parent) throw new NotFoundError('folders', parentId);
    if (parent.kind !== existing.kind) {
      throw new FolderError('invalid', `That folder is in ${parent.kind}, not ${existing.kind}`);
    }
    if (!canMoveFolder(all, id, parentId)) {
      throw new FolderError('cycle', 'A folder cannot be moved inside itself');
    }
  }

  const patch = {
    parentId,
    updatedAt: context.clock.now().toISOString(),
    updatedBy: context.uid,
  };
  await repo.update(id, patch);
  return { ...existing, ...patch };
}

/**
 * Delete an empty folder.
 *
 * A folder holding anything is refused rather than cascaded. Deleting a
 * folder is a tidying action and people take it lightly; deleting the files
 * inside it is not recoverable, needs `files: admin` rather than
 * `files: edit`, and is never what somebody reorganising a tree meant. The
 * refusal names what is in the way so it can be dealt with deliberately.
 */
export async function deleteFolder(
  context: FolderContext,
  jobId: string,
  id: string,
): Promise<void> {
  const repo = folders(context, jobId);
  const existing = await repo.get(id);
  if (!existing) throw new NotFoundError('folders', id);
  if (existing.system) throw new FolderError('system', `"${existing.name}" cannot be deleted`);

  const all = await listFolders(context, jobId);
  const children = all.filter((folder) => folder.parentId === id);
  if (children.length > 0) {
    throw new FolderError(
      'notEmpty',
      `"${existing.name}" still holds ${children.length} ${children.length === 1 ? 'folder' : 'folders'}`,
    );
  }

  const held = await files(context).list({
    where: [{ field: 'folderId', op: '==', value: id }],
  });
  if (held.items.length > 0) {
    const count = held.items.length;
    throw new FolderError(
      'notEmpty',
      `"${existing.name}" still holds ${count} ${count === 1 ? 'file' : 'files'}`,
    );
  }

  await repo.delete(id);
}

// ---- pure tree shaping ---------------------------------------------------

export interface FolderNode {
  readonly folder: Folder;
  readonly children: readonly FolderNode[];
  readonly depth: number;
}

/**
 * The folders of one root as a tree.
 *
 * A folder whose parent is missing — deleted out from under it, or in
 * another root — is returned at the top level rather than dropped. A tree
 * that silently omits rows is a tree that loses files, and the records are
 * still there to be found.
 *
 * A cycle in stored data terminates instead of hanging. Nothing should be
 * able to write one, which is exactly why a view must not depend on that.
 */
export function folderTree(folders: readonly Folder[], kind: FolderKind): FolderNode[] {
  const inKind = folders.filter((folder) => folder.kind === kind);
  const byId = new Map(inKind.map((folder) => [folder.id, folder] as const));
  const children = new Map<string, Folder[]>();
  const roots: Folder[] = [];

  for (const folder of inKind) {
    const parentId = folder.parentId;
    if (parentId === undefined || !byId.has(parentId) || parentId === folder.id) {
      roots.push(folder);
      continue;
    }
    const siblings = children.get(parentId);
    if (siblings) siblings.push(folder);
    else children.set(parentId, [folder]);
  }

  const byName = (a: Folder, b: Folder) => a.name.localeCompare(b.name);
  const seen = new Set<string>();
  const build = (folder: Folder, depth: number): FolderNode => {
    seen.add(folder.id);
    const kids = (children.get(folder.id) ?? [])
      .filter((child) => !seen.has(child.id))
      .sort(byName)
      .map((child) => build(child, depth + 1));
    return { folder, children: kids, depth };
  };

  const nodes = roots.sort(byName).map((folder) => build(folder, 0));

  // Anything the walk never reached is in a cycle: every folder in a ring
  // has a parent that exists, so none of them qualified as a root and the
  // whole ring would be missing from the tree. Lifting the remainder to the
  // top is the same promise as for an orphan — a tree that silently omits
  // rows is a tree that loses files.
  const stranded = inKind.filter((folder) => !seen.has(folder.id)).sort(byName);
  for (const folder of stranded) {
    if (seen.has(folder.id)) continue;
    nodes.push(build(folder, 0));
  }
  return nodes;
}

/** Every folder flattened in display order, so a tree renders as rows. */
export function flattenTree(nodes: readonly FolderNode[]): FolderNode[] {
  return nodes.flatMap((node) => [node, ...flattenTree(node.children)]);
}

/**
 * The path from a root down to one folder, ancestors first.
 *
 * Stops at a missing ancestor rather than throwing: a breadcrumb is a
 * navigation aid, and half a trail still gets somebody home.
 */
export function breadcrumb(folders: readonly Folder[], folderId?: string): Folder[] {
  if (folderId === undefined) return [];
  const byId = new Map(folders.map((folder) => [folder.id, folder] as const));
  const trail: Folder[] = [];
  const seen = new Set<string>();
  let current = byId.get(folderId);
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    trail.unshift(current);
    current = current.parentId === undefined ? undefined : byId.get(current.parentId);
  }
  return trail;
}

/** Every folder beneath one, at any depth. */
export function descendantIds(folders: readonly Folder[], id: string): string[] {
  const found: string[] = [];
  const seen = new Set<string>([id]);
  let frontier = [id];
  while (frontier.length > 0) {
    const next: string[] = [];
    for (const folder of folders) {
      if (folder.parentId === undefined) continue;
      if (!frontier.includes(folder.parentId)) continue;
      if (seen.has(folder.id)) continue;
      seen.add(folder.id);
      found.push(folder.id);
      next.push(folder.id);
    }
    frontier = next;
  }
  return found;
}

/** Whether a destination is legal: not the folder itself, nor inside it. */
export function canMoveFolder(folders: readonly Folder[], id: string, parentId?: string): boolean {
  if (parentId === undefined) return true;
  if (parentId === id) return false;
  return !descendantIds(folders, id).includes(parentId);
}
